import {Participant, AgreementClause, isGuestIncompleteStatus} from '@chekin/core';
import {
  GuestsData,
  ValidateGuestCountProps,
  InstantCheckInFormTypes,
  BuildGuestInstantPayloadProps,
} from '@chekin/shared';
import {AgreementClauseKeyFormName} from 'utils/guestForm/constants';
import {isAdult} from 'utils/common';
import {
  CreateGuestByParticipantPayload,
  CreateGuestByParticipantResponse,
} from '@chekin/api';

async function postMultipleItems<
  PayloadType = unknown,
  ErrorType = unknown,
  ResponseType = unknown,
>(
  payload: PayloadType[],
  mutateAsync: ({payload}: {payload: PayloadType}) => Promise<ResponseType>,
) {
  const promises = payload.map(itemPayload => mutateAsync({payload: itemPayload}));
  const results = await Promise.allSettled(promises);
  const success: ResponseType[] = [];
  results.forEach(result => {
    if (result.status === 'fulfilled') {
      success.push(result.value);
    }
  });

  const failed = results
    .map((result, index) => ({result, itemData: payload[index]}))
    .filter(({result}) => result.status === 'rejected')
    .map(({result, itemData}) => {
      if (result.status === 'rejected') {
        return {
          item: itemData,
          error: result.reason as ErrorType,
        };
      }
      return null;
    })
    .filter(item => item !== null);

  return {
    failed,
    success,
  };
}

function buildAgreementClausesStateData(
  clauses?: AgreementClause[],
  formClauses?: Record<string, boolean>,
): GuestsData['agreement_clauses'] | undefined {
  if (!clauses || !formClauses) {
    return undefined;
  }

  return clauses.filter(clause => {
    const name = `${AgreementClauseKeyFormName}${clause.id}`;
    return Boolean(formClauses[name]);
  });
}

function isDegradedGuestResult(result: CreateGuestByParticipantResponse) {
  const hasFieldErrors = Boolean(result.errors) && Object.keys(result.errors).length > 0;
  return hasFieldErrors || isGuestIncompleteStatus(result.data?.guestapp_status);
}

function countDegradedGuestResults(guests: CreateGuestByParticipantResponse[]) {
  return guests.filter(isDegradedGuestResult).length;
}

function buildAgreementClausesData(
  isLeader: boolean,
  clauses?: GuestsData['agreement_clauses'],
) {
  if (!clauses || clauses.length === 0) {
    return undefined;
  }

  return clauses.flatMap(clause => {
    const shouldIncludeClause = isLeader || !clause?.is_leaders_field;

    if (shouldIncludeClause && clause.id) {
      return [clause.id];
    }

    return [];
  });
}

function buildGuestInstantPayload({
  participant,
  guestsData,
  leaderGuestId,
  reservationId = '',
}: BuildGuestInstantPayloadProps): CreateGuestByParticipantPayload {
  const isLeader = participant.id === leaderGuestId;
  const agreement_clauses = buildAgreementClausesData(
    isLeader,
    guestsData.agreement_clauses,
  );

  return {
    full_name: `${participant.name ?? ''} ${participant.surname ?? ''}`,
    participant_id: participant.id,
    reservation_id: reservationId,
    is_leader: isLeader,
    ...guestsData,
    agreement_clauses,
  };
}

function validateGuestCount({
  totalParticipants = 0,
  remainingAllowedGuest = 0,
  isNumberOfGuestsEditable = true,
}: ValidateGuestCountProps) {
  if (isNumberOfGuestsEditable) {
    return true;
  }
  const isExceededNumberOfParticipants = totalParticipants > remainingAllowedGuest;

  return !isExceededNumberOfParticipants;
}

function buildStateFormData(
  participants: Participant[] = [],
  clauses: GuestsData['agreement_clauses'],
  formData: InstantCheckInFormTypes,
) {
  const agreement_clauses = buildAgreementClausesStateData(
    clauses,
    formData['agreement_clauses'],
  );
  return {
    participants,
    guestsData: {
      agreement_clauses,
      contract_email: formData['contract_email'],
      send_copy_contract_email: formData['send_copy_contract_email'],
    },
  };
}

const isValidChildrenRegistration = (
  participants: Participant[],
  remainingAllowedGuest: number,
  hasLeaderGuest: boolean,
) => {
  const isAnyParticipantAnAdult = participants.some(participant =>
    isAdult(participant.birth_date),
  );

  if (
    hasLeaderGuest ||
    isAnyParticipantAnAdult ||
    (!isAnyParticipantAnAdult && remainingAllowedGuest > participants.length)
  ) {
    return true;
  }

  return false;
};

export {
  buildGuestInstantPayload,
  isAdult,
  postMultipleItems,
  buildAgreementClausesStateData,
  buildAgreementClausesData,
  isDegradedGuestResult,
  countDegradedGuestResults,
  validateGuestCount,
  buildStateFormData,
  isValidChildrenRegistration,
};
